import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Search, MoreHorizontal, MonitorPlay } from 'lucide-react';
import {
  Badge,
  Input,
  IconButton,
  SegmentedControl,
  Skeleton,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Toolbar } from '@vnetwork/ui/layout';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useAesSessions,
  useRevokeAesSession,
  useRevokeAesSessions,
  type AesSession,
} from '@/api/aes';
import { trackFeatureAction } from '@/lib/analytics-events';
import styles from './Aes.module.css';

/** Kebab row-action menu housing the (destructive) revoke item — mirrors the
 *  /teams members table idiom (ghost MoreHorizontal → dropdown → danger item). */
function RevokeMenu({ label, onRevoke }: { label: string; onRevoke: () => void }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <IconButton variant="ghost" size="sm" aria-label="Session actions">
            <MoreHorizontal size={16} />
          </IconButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem tone="danger" onSelect={onRevoke}>
            {label}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

const kindTone = (k?: string): 'primary' | 'accent' | 'neutral' =>
  k === 'live' ? 'primary' : k === 'vod' ? 'accent' : 'neutral';

const clock = (unixSeconds?: number) =>
  unixSeconds ? dayjs.unix(unixSeconds).format('HH:mm:ss') : '—';

/** One (client IP + stream + kind) bucket — collapses a server that re-signs the
 *  same stream into a single revocable row. */
interface SessionGroup {
  key: string;
  ip?: string;
  stream_id: string;
  kind?: string;
  count: number;
  ids: string[];
  newest: number;
}

function groupSessions(sessions: AesSession[]): SessionGroup[] {
  const map = new Map<string, SessionGroup>();
  for (const s of sessions) {
    const key = `${s.ip ?? '—'}|${s.stream_id}|${s.kind ?? '—'}`;
    let g = map.get(key);
    if (!g) {
      g = { key, ip: s.ip, stream_id: s.stream_id, kind: s.kind, count: 0, ids: [], newest: 0 };
      map.set(key, g);
    }
    g.count += 1;
    g.ids.push(s.id);
    g.newest = Math.max(g.newest, s.issued_at ?? 0);
  }
  return [...map.values()].sort((a, b) => b.count - a.count);
}

const matches = (s: AesSession, q: string) =>
  !q || [s.ip, s.stream_id, s.id, s.kind].some((v) => (v ?? '').toLowerCase().includes(q));

type Pending =
  | { type: 'one'; session: AesSession }
  | { type: 'group'; group: SessionGroup }
  | null;

/**
 * Active DRM sessions. Defaults to a per-(client IP + stream) rollup because a
 * single partner backend that re-signs one stream produces many sessions — the
 * grouped view collapses those into one revocable row; switch to "All sessions"
 * for the flat list with per-session revoke.
 */
export function AesSessions({ serviceId }: { serviceId?: string }) {
  const sid = serviceId ?? '';
  const { data, isLoading, isError, error } = useAesSessions(serviceId);
  const revoke = useRevokeAesSession(sid);
  const revokeMany = useRevokeAesSessions(sid);
  const { show } = useToast();

  const [view, setView] = useState<'grouped' | 'all'>('grouped');
  const [q, setQ] = useState('');
  const [pending, setPending] = useState<Pending>(null);

  const sessions = useMemo(
    () => (data?.sessions ?? []).filter((s) => matches(s, q.trim().toLowerCase())),
    [data, q],
  );
  const groups = useMemo(() => groupSessions(sessions), [sessions]);

  const groupColumns = useMemo<ColumnDef<SessionGroup, unknown>[]>(
    () => [
      {
        id: 'kind',
        header: 'Kind',
        size: 72,
        cell: ({ row }) => <Badge tone={kindTone(row.original.kind)} appearance="subtle" size="sm">{row.original.kind ?? '—'}</Badge>,
      },
      { accessorKey: 'stream_id', header: 'Stream', size: 110 },
      {
        accessorKey: 'ip',
        header: 'Client IP',
        size: 140,
        cell: ({ row }) => row.original.ip ?? <span className={styles.cellMuted}>—</span>,
      },
      {
        accessorKey: 'count',
        header: 'Sessions',
        size: 90,
        cell: ({ row }) => (
          <Badge tone={row.original.count >= 20 ? 'warning' : 'neutral'} appearance="subtle" size="sm">{row.original.count}</Badge>
        ),
      },
      {
        accessorKey: 'newest',
        header: 'Latest',
        size: 84,
        cell: ({ row }) => <span className={styles.cellMuted}>{clock(row.original.newest)}</span>,
      },
      {
        id: 'actions',
        header: '',
        size: 56,
        enableSorting: false,
        cell: ({ row }) => (
          <RevokeMenu
            label={`Revoke all ${row.original.count} sessions`}
            onRevoke={() => setPending({ type: 'group', group: row.original })}
          />
        ),
      },
    ],
    [],
  );

  const flatColumns = useMemo<ColumnDef<AesSession, unknown>[]>(
    () => [
      {
        accessorKey: 'kind',
        header: 'Kind',
        size: 72,
        cell: ({ row }) => <Badge tone={kindTone(row.original.kind)} appearance="subtle" size="sm">{row.original.kind ?? '—'}</Badge>,
      },
      { accessorKey: 'stream_id', header: 'Stream', size: 110 },
      {
        accessorKey: 'ip',
        header: 'Client IP',
        size: 120,
        cell: ({ row }) => row.original.ip ?? <span className={styles.cellMuted}>—</span>,
      },
      {
        accessorKey: 'id',
        header: 'Session',
        cell: ({ row }) => <span className={styles.eventName}>{row.original.id}</span>,
      },
      {
        accessorKey: 'issued_at',
        header: 'Issued',
        size: 84,
        cell: ({ row }) => <span className={styles.cellMuted}>{clock(row.original.issued_at)}</span>,
      },
      {
        id: 'actions',
        header: '',
        size: 56,
        enableSorting: false,
        cell: ({ row }) => (
          <RevokeMenu
            label="Revoke session"
            onRevoke={() => setPending({ type: 'one', session: row.original })}
          />
        ),
      },
    ],
    [],
  );

  if (isError) {
    return (
      <EmptyState
        title="Sessions unavailable"
        description={
          (error as { response?: { status?: number } })?.response?.status === 503
            ? 'The gateway session registry is disabled (SESSION_REGISTRY_ENABLED=false).'
            : ((error as { message?: string })?.message ?? 'Could not load active sessions.')
        }
      />
    );
  }

  if (isLoading && !data) {
    return <Skeleton variant="rect" height={140} />;
  }

  const busy = revoke.isPending || revokeMany.isPending;
  const confirm = buildConfirm(pending);

  return (
    <>
      <Toolbar
        start={
          <SegmentedControl
            value={view}
            onValueChange={(v) => setView(v as 'grouped' | 'all')}
            items={[
              { value: 'grouped', label: 'Grouped by client' },
              { value: 'all', label: 'All sessions' },
            ]}
          />
        }
        end={
          <Input
            className={styles.search}
            startSlot={<Search />}
            placeholder="Filter IP / stream / kind / id…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        }
      />

      {view === 'grouped' ? (
        <DataTable
          columns={groupColumns}
          data={groups}
          getRowId={(g) => g.key}
          dense
          emptyState={<EmptyState size="sm" icon={<MonitorPlay size={20} />} title="No active sessions" description="Sessions appear while streams are being watched." />}
        />
      ) : (
        <DataTable
          columns={flatColumns}
          data={sessions}
          getRowId={(r) => r.id}
          dense
          emptyState={<EmptyState size="sm" icon={<MonitorPlay size={20} />} title="No active sessions" description="Sessions appear while streams are being watched." />}
        />
      )}

      {data && (
        <div className={styles.meta}>
          <span>
            {data.count} session{data.count === 1 ? '' : 's'} across {groups.length} client
            {groups.length === 1 ? '' : 's'}
          </span>
        </div>
      )}

      <ConfirmDialog
        open={pending != null}
        onOpenChange={(o) => !o && setPending(null)}
        title={confirm.title}
        description={confirm.description}
        confirmLabel={confirm.confirmLabel}
        destructive
        loading={busy}
        onConfirm={() => {
          if (!pending) return;
          const done = (title: string) => {
            show({ tone: 'success', title });
            setPending(null);
          };
          if (pending.type === 'one') {
            revoke.mutate(pending.session.id, {
              onSuccess: () => {
                trackFeatureAction('aes', 'session', 'delete');
                done('Session revoked');
              },
              onError: () => show({ tone: 'danger', title: 'Could not revoke session' }),
            });
          } else {
            revokeMany.mutate(pending.group.ids, {
              onSuccess: () => {
                trackFeatureAction('aes', 'session', 'delete');
                done(`Revoked ${pending.group.count} sessions`);
              },
              onError: () => show({ tone: 'danger', title: 'Some revokes failed' }),
            });
          }
        }}
      />
    </>
  );
}

function buildConfirm(pending: Pending) {
  if (pending?.type === 'group') {
    const g = pending.group;
    return {
      title: `Revoke all ${g.count} sessions?`,
      description: `Immediately invalidates every key for ${g.ip ?? 'unknown IP'} · ${g.stream_id} (${g.kind ?? '?'}). That client must re-authorize.`,
      confirmLabel: `Revoke ${g.count}`,
    };
  }
  if (pending?.type === 'one') {
    const s = pending.session;
    return {
      title: 'Revoke session?',
      description: `Revoking immediately invalidates the key for session ${s.id} (${s.kind ?? '?'} · ${s.stream_id}). The player must re-authorize.`,
      confirmLabel: 'Revoke',
    };
  }
  return { title: 'Revoke session?', description: '', confirmLabel: 'Revoke' };
}

export default AesSessions;
